import { faCamera } from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { FileUploader } from 'src/components/FileUploader';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

interface ImageUploaderProps {
  title: string;
  subtitle?: string;
  onImageSelect: (file: File) => void;
  avatarSrc?: string;
  isAvatar?: boolean;
  width?: number;
  height?: number;
  imageHeight?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  title,
  subtitle,
  onImageSelect,
  avatarSrc: initialAvatarSrc,
  isAvatar = true,
  width = 300,
  height = 200,
  imageHeight,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'imageUploader' });
  const [previewSrc, setPreviewSrc] = useState<string | undefined>(
    initialAvatarSrc
  );

  useEffect(() => {
    setPreviewSrc(initialAvatarSrc);
  }, [initialAvatarSrc]);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      onImageSelect(file);

      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewSrc(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box display="flex" alignItems="center">
      {isAvatar ? (
        <Avatar
          src={previewSrc}
          sx={{
            mr: 3,
            bgcolor: previewSrc ? undefined : 'info.50',
            border: (theme) =>
              previewSrc
                ? undefined
                : `4px solid ${theme.palette.background.paper}`,
            outline: previewSrc ? undefined : '1px dashed',
            outlineColor: (theme) =>
              previewSrc ? undefined : theme.palette.info[300],
            outlineOffset: previewSrc ? undefined : '-1px',
          }}
          size="xlarge"
        >
          {!previewSrc && (
            <Icon icon={faCamera} size="medium" color="primary" />
          )}
        </Avatar>
      ) : (
        <Box
          sx={{
            mr: 3,
            width,
            height,
            bgcolor: previewSrc ? undefined : 'info.50',
            border: (theme) =>
              previewSrc
                ? undefined
                : `4px solid ${theme.palette.background.paper}`,
            outline: previewSrc ? undefined : '1px dashed',
            outlineColor: (theme) =>
              previewSrc ? undefined : theme.palette.info[300],
            outlineOffset: previewSrc ? undefined : '-1px',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {previewSrc ? (
            <Box
              component="img"
              src={previewSrc}
              sx={{
                width: '100%',
                height: imageHeight || '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Icon icon={faCamera} size="medium" color="primary" />
          )}
        </Box>
      )}
      <Stack spacing={1}>
        <Typography variant="body1">{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        <FileUploader
          accept="image/*"
          onFileSelect={handleFileSelect}
          multiple={false}
          trigger={
            <Button variant="bordered" color="primary">
              {t('select')}
            </Button>
          }
        />
      </Stack>
    </Box>
  );
};
